import { IModel, ITypedModel } from 'core-ts-types';
import { IRegistrationSection } from '../../../interfaces/scheduler_interfaces';
import { ISection } from '../../../interfaces/scheduler_interfaces';
import * as _4 from 'lodash4';
import { TermCourseServant } from '../../term_courses/servants/term_course_servant';
import { MultiSectionServant } from '../../course_scheduler/servants/multi_section_servant';

export interface IRegistrationPins {
  registeredSectionPins: Record<string, string>;
  scheduledSectionPins: Record<string, string>;
}

export interface ISectionPin {
  section: ISection;
  pin: string;
}
export interface IPinSection {
  sectionId: number;
  courseId: number;
  is_selected: boolean;
  status: string;
}
export interface IRegistrationPinsModel {
  _registration_pins: IRegistrationPins;
  _registration_pins_sections: IPinSection[];
}
export class RegistrationPinsModel implements ITypedModel<IRegistrationPinsModel> {
  private _registration_pins: IRegistrationPins;
  private _registration_pins_sections: IPinSection[];
  private registerStatusFlags = ['REGISTERED', 'IN_PROGRESS'];

  constructor(private ngTermCourseServant: TermCourseServant, private ngMultiSectionServant: MultiSectionServant) {
    this._registration_pins = { registeredSectionPins: {}, scheduledSectionPins: {} };
  }

  initialize(sections: ISection[]) {
    this.clear();
  }

  clear() {
    this._registration_pins = { registeredSectionPins: {}, scheduledSectionPins: {} };
    return this;
  }
  update(): RegistrationPinsModel {
    return this;
  }
  getPin(sectionId: number): string {
    const allPins = {
      ...this._registration_pins.registeredSectionPins,
      ...this._registration_pins.scheduledSectionPins
    };
    return _4.get(allPins, sectionId, '');
  }
  _getRegisterSelectedSectionIds(): number[] {
    const allSelectedRegisteredSections = _4.compact(
      _4.map(
        _4.filter(
          this._registration_pins_sections,
          (section: IPinSection) => _4.includes(this.registerStatusFlags, section.status) && section.is_selected
        ),
        (section: IPinSection) => section.sectionId
      )
    );
    return allSelectedRegisteredSections;
  }
  _getScheduledSelectedSectionIds(): number[] {
    const allSelectedScheduledSections = _4.compact(
      _4.map(
        _4.filter(
          this._registration_pins_sections,
          (section: IPinSection) => !_4.includes(this.registerStatusFlags, section.status) && section.is_selected
        ),
        (section: IPinSection) => section.sectionId
      )
    );
    return allSelectedScheduledSections;
  }

  _getRegisterSelectedSectionPins() {
    const allPins = {
      ...this._registration_pins.registeredSectionPins,
      ...this._registration_pins.scheduledSectionPins
    };
    return _4.pick(allPins, this._getRegisterSelectedSectionIds());
  }
  _getScheduledSelectedSectionPins() {
    const allPins = {
      ...this._registration_pins.registeredSectionPins,
      ...this._registration_pins.scheduledSectionPins
    };
    return _4.pick(allPins, this._getScheduledSelectedSectionIds());
  }

  hasRegisterSelectedSectionErrors(): boolean {
    const registerSelectedSectionPinKeys = _4.compact(
      _4.map(
        _4.sortBy(_4.keys(_4.pickBy(this._getRegisterSelectedSectionPins(), (pin: string) => pin !== ''))),
        (sectionId: string) => _4.toNumber(sectionId)
      )
    );
    const registerSelectedSectionIds = _4.sortBy(this._getRegisterSelectedSectionIds());
    return !_4.isEqual(registerSelectedSectionIds, registerSelectedSectionPinKeys);
  }
  hasScheduledSelectedSectionErrors(): boolean {
    const scheduledSelectedSectionPinKeys = _4.compact(
      _4.map(
        _4.sortBy(_4.keys(_4.pickBy(this._getScheduledSelectedSectionPins(), (pin: string) => pin !== ''))),
        (sectionId: string) => _4.toNumber(sectionId)
      )
    );
    const scheduledSelectedSectionIds = _4.sortBy(this._getScheduledSelectedSectionIds());
    return !_4.isEqual(scheduledSelectedSectionIds, scheduledSelectedSectionPinKeys);
  }
  getCourse(sectionId: number) {
    const sectionsBySectionIds = _4.find(
      this._registration_pins_sections,
      (section: IPinSection) => section.sectionId === sectionId
    );
    return sectionsBySectionIds ? sectionsBySectionIds.courseId : undefined;
  }
  getRegisterSectionsInfo() {
    const _courseIds = _4.map(this._getRegisterSelectedSectionIds(), (sectionId) => this.getCourse(sectionId));
    const courseIds = _4.uniq(_courseIds.filter((courseId): courseId is number => _4.isEmpty(courseId)));
    return {
      sections: this._getRegisterSelectedSectionIds(),
      courseIds,
      section_pins: this._getRegisterSelectedSectionPins()
    };
  }
  getScheduledSectionsInfo() {
    return {
      sections: this._getScheduledSelectedSectionIds(),
      section_pins: this._getScheduledSelectedSectionPins()
    };
  }

  updateRegisteredSectionPin(sectionPin: ISectionPin): void {
    this._updateSectionPin(sectionPin, 'registeredSectionPins');
  }

  updateScheduledSectionPin(sectionPin: ISectionPin): void {
    this._updateSectionPin(sectionPin, 'scheduledSectionPins');
  }
  read(): IRegistrationPinsModel {
    return {
      _registration_pins: this._registration_pins,
      _registration_pins_sections: this._registration_pins_sections
    };
  }
  write(model: Partial<IRegistrationPinsModel>) {
    if (model._registration_pins_sections) {
      this._registration_pins_sections = model._registration_pins_sections;
    }
    return this;
  }

  isSectionSelected(sectionId: number): boolean {
    return _4.get(_4.find(this._registration_pins_sections, { sectionId }), 'is_selected', false);
  }

  toggleSelection(section: ISection, status: boolean): void {
    const relatedSectionsIds = _4.compact(
      _4.map(
        _4.filter(this.ngMultiSectionServant.getScheduledSiblingSections(section), (relatedSection: ISection) =>
          _4.isEqual(section.session, relatedSection.session)
        ),
        'id'
      )
    );
    if (relatedSectionsIds.length) {
      _4.forEach(relatedSectionsIds, (sectionId: number) => {
        this._changeSectionSelectionStatus(sectionId, status);
      });
    } else {
      this._changeSectionSelectionStatus(section.id, status);
    }
  }

  private _updateSectionPin(sectionPin: ISectionPin, sectionPinType: string): void {
    const isMultiSection = TermCourseServant.isMultiSectionCourse(sectionPin.section._course);
    if (isMultiSection) {
      this._saveRegistrationPins(sectionPinType, sectionPin);
    } else {
      this._registration_pins[sectionPinType][sectionPin.section.id] = sectionPin.pin;
    }
  }

  private _changeSectionSelectionStatus(sectionId: number, status: boolean) {
    _4.find(this._registration_pins_sections, { sectionId }).is_selected = status;
  }

  private _saveRegistrationPins(sectionType: string, sectionPin: ISectionPin): void {
    const relatedSectionsIds = _4.compact(
      _4.map(this.ngMultiSectionServant.getScheduledSiblingSections(sectionPin.section), 'id')
    );
    _4.forEach(relatedSectionsIds, (sectionId: number) => {
      this._registration_pins[sectionType][sectionId] = sectionPin.pin;
    });
  }
}

angular.module('capability-ng-scheduler').service('ngRegistrationPinsModel', RegistrationPinsModel);



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-scheduler/src/views/registration/models/registration_pins_model.ts